<template>
  <div :class="`input-wrapper ${id} ${hasErrors ? 'has-errors' : ''}`">
    <!-- input label -->
    <label v-if="label" :class="['input-label', { 'visually-hidden': hideLabel }]" :for="id">
      <span>{{ label }}</span>
    </label>
    <!-- input element -->
    <div class="input-el-wrapper">
      <textarea
        :id="id"
        :disabled="isDisabled"
        class="input"
        v-model="valueRef"
        :data-value="valueRef"
        :placeholder="placeholder"
        :aria-label="controlAriaLabel"
        @input="onInput"
        @keydown="onKeyDown"
        @keyup="onKeyUp"
        @click="onClick"
      ></textarea>
    </div>
    <div v-if="errors" class="input-errors-wrapper">{{ errors }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import debounce from '@/utils/misc/debounce'

interface InputPropsType {
  id: string
  errors?: string
  label?: string
  hideLabel?: boolean
  ariaLabel?: string
  placeholder: string
  value?: string | null
  isDisabled?: boolean
  onChangeDebounceDelay?: number
}

const props = defineProps<InputPropsType>()

// set default value v-model
const valueRef = ref(props.value || '')

watch(
  () => props.value,
  (newVal) => {
    valueRef.value = newVal ?? ''
  },
)

defineExpose({
  clear() {
    valueRef.value = ''
  },
  set(message: string) {
    valueRef.value = message
  },
})

const emit = defineEmits(['emit:change', 'emit:onkeydown', 'emit:onkeyup', 'emit:click'])

const onInput = (event: Event) => {
  const value = (event.target as HTMLInputElement).value
  valueRef.value = value

  if (props.onChangeDebounceDelay === 0) {
    emit('emit:change', value)
  } else {
    onChange(event)
  }
}

const onChange = debounce(
  (event: Event) => {
    const value = (event.target as HTMLInputElement).value
    emit('emit:change', value)
  },
  props.onChangeDebounceDelay ?? 500,
  false,
)

const onKeyDown = (event: KeyboardEvent) => {
  emit('emit:onkeydown', event)
}

const onKeyUp = (event: KeyboardEvent) => {
  emit('emit:onkeyup', event)
}

const onClick = (event: MouseEvent) => {
  emit('emit:click', event)
}

const hasErrors = computed(() => !!props.errors)
const controlAriaLabel = computed(() => (props.label ? undefined : props.ariaLabel || props.placeholder))
</script>

<style scoped lang="scss">
@import './custom-textarea.scss';
</style>
